import { callStatedList, updateEachListItem, withConfirmationService, withListState, withModalService } from "@aston/foundation/signals";
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from "@ngrx/signals";
import { IFilterOperator, PortalRequestStatus } from "@aston/foundation/enums";
import { computed, effect, inject, untracked } from "@angular/core";
import { delay, of } from "rxjs";

import { OpenRequestModalComponent } from "../../../components/open-request-modal/open-request-modal.component";
import { SuperDebtorsRequestsListFilterSpecService } from "../../../services/requests-filter-spec.service";
import { PortalRequestsListSortProp } from "../../../enums/portal-requests-list-sort-prop.enum";
import { PortalRequestsService } from "../../../services/portal-requests.service";
import { PortalRequest } from "../../../models/request.model";
import { AppConstants } from "../../../../app.constants";

export const REQUESTS = 'debtorsRequests'

export const RequestsListPageStore = signalStore(
	{ providedIn: 'root' },

	withState({
		showInactiveRequests: false
	}),

	withModalService(),
	withConfirmationService(),

	withListState<PortalRequest, PortalRequestsListSortProp, typeof REQUESTS>({
		defaultSort: [{prop: PortalRequestsListSortProp.RequestDate, isDesc: true}],
		defaultPageSize: 5,
		collection: REQUESTS,
	}),

	withComputed((store, specs = inject(SuperDebtorsRequestsListFilterSpecService)) => ({
		debtorsRequestsListFullState: computed(() => ({
			...store.debtorsRequestsListState(),
			filters: (store.debtorsRequestsListState().filters || []).concat(
				store.showInactiveRequests() ? [] : specs.byStatuses(IFilterOperator.Among, PortalRequestStatus.Requested)
			)
		})),
	})),

	withMethods((
		store,
		reqs = inject(PortalRequestsService)
	) => ({
		loadRequests: () => callStatedList(reqs.listRequests(store.debtorsRequestsListFullState()), store, REQUESTS),
		toggleInactiveRequests: (showInactiveRequests = !store.showInactiveRequests()) => {
			patchState(store, { showInactiveRequests })
			store.debtorsRequestsSetPaging({ page: 1 })
		},
		openDebtorsRequestModal: (req: PortalRequest) => {
			const modal = store.openModal(OpenRequestModalComponent, AppConstants.DEFAULT_MODAL_OPTIONS)
			const component = (modal.componentInstance as OpenRequestModalComponent);
			component.readonly.set(true)
			component.loadFromRequest(req)
		},
		openDebtorsRequestDeleteModal: (req: PortalRequest) => {
			store.confirm(
				{
					textsKey: `PortalRequests.Confirmation.Delete.`,
					confirmBtnText: 'Actions.Validate',
					cancelBtnText: 'Actions.Cancel',
				},
				() => callStatedList(reqs.cancelRequest(req.portalRequestId), store, REQUESTS, { stateOnly: true })
					.then(() => store.debtorsRequestsSetPaging({ page: 1 }))
			)
		},
		toggleDebtorsRequestsListRow: (req: PortalRequest) => patchState(store, updateEachListItem<PortalRequest, typeof REQUESTS>(
			r => r.portalRequestId === req.portalRequestId
				? ({ ...r, accountingDocumentsExpandOpened: !r.accountingDocumentsExpandOpened })
				: r,
			REQUESTS
		))
	})),

	withHooks({
		onInit: store => {
			effect(() => {
				const first = untracked(store.debtorsRequestsCallState) === 'init'
				store.debtorsRequestsPaging()
				store.debtorsRequestsFilters()
				// then
				if (!first) untracked(store.loadRequests)
			})
		},
	})
)

export type RequestsListPageStore = InstanceType<typeof RequestsListPageStore>;
